import React, { useEffect, useState } from 'react';
import { taskService } from '../../services/taskService';
import { Level } from '../../types';
import { useToast } from '../../components/Toast';

type NewLevelForm = {
  level_number: number;
  required_points: number;
  name: string;
  description: string;
  features: string[];
};

const initialNewLevel: NewLevelForm = {
  level_number: 1,
  required_points: 0,
  name: '',
  description: '',
  features: [],
};

// Danh sách tính năng phổ biến - CHỈ CÁC TÍNH NĂNG THỰC SỰ CÓ TRONG WEB
const COMMON_FEATURES = [
  { value: 'unlock_premium_courses', label: 'Mở khóa khóa học Premium' }, // Có courses với price > 0 (courses.py)
  { value: 'unlock_custom_avatar', label: 'Mở khóa avatar tùy chỉnh' }, // Có upload avatar (users.py /avatar endpoint)
  { value: 'unlock_priority_support', label: 'Hỗ trợ ưu tiên' }, // Có support chat (support_chat.py)
  { value: 'unlock_private_messages', label: 'Tin nhắn riêng tư' }, // Có chat (chat.py)
  { value: 'unlock_create_posts', label: 'Đăng bài viết' }, // Có posts (posts.py)
  { value: 'unlock_follow_users', label: 'Theo dõi người dùng' }, // Có follow (users.py /follow)
  { value: 'unlock_comment_posts', label: 'Bình luận bài viết' }, // Có comments (posts.py /comments)
  { value: 'unlock_like_posts', label: 'Thích bài viết' }, // Có likes (posts.py /like)
];

const AdminLevelsPage: React.FC = () => {
  const { showToast } = useToast();
  const [levels, setLevels] = useState<Level[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newLevel, setNewLevel] = useState<NewLevelForm>(initialNewLevel);
  const [editingLevelId, setEditingLevelId] = useState<number | null>(null);
  const [editingData, setEditingData] = useState<NewLevelForm>(initialNewLevel);
  const [savingEdit, setSavingEdit] = useState(false);

  const fetchLevels = async () => {
    setLoading(true);
    try {
      const data = await taskService.listLevelsAdmin();
      setLevels(data.levels);
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Không thể tải danh sách cấp độ', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLevels();
  }, []);

  const handleCreateLevel = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        level_number: parseInt(String(newLevel.level_number)),
        required_points: parseInt(String(newLevel.required_points)),
        name: newLevel.name.trim() || undefined,
        description: newLevel.description.trim() || undefined,
        features: newLevel.features.length > 0 ? newLevel.features : undefined,
      };

      const { level } = await taskService.createLevel(payload);
      setLevels((prev) => [...prev, level].sort((a, b) => a.level_number - b.level_number));
      setNewLevel(initialNewLevel);
      showToast('Tạo cấp độ thành công', 'success');
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Không thể tạo cấp độ', 'error');
    } finally {
      setCreating(false);
    }
  };

  const addFeature = (featureValue: string) => {
    if (!newLevel.features.includes(featureValue)) {
      setNewLevel((prev) => ({ ...prev, features: [...prev.features, featureValue] }));
    }
  };

  const removeFeature = (featureValue: string) => {
    setNewLevel((prev) => ({ ...prev, features: prev.features.filter((f) => f !== featureValue) }));
  };

  const addCustomFeature = (featureName: string) => {
    if (featureName.trim() && !newLevel.features.includes(featureName.trim())) {
      setNewLevel((prev) => ({ ...prev, features: [...prev.features, featureName.trim()] }));
    }
  };

  const startEdit = (level: Level) => {
    setEditingLevelId(level.id);
    setEditingData({
      level_number: level.level_number,
      required_points: level.required_points,
      name: level.name || '',
      description: level.description || '',
      features: level.features || [],
    });
  };

  const cancelEdit = () => {
    setEditingLevelId(null);
    setEditingData(initialNewLevel);
  };

  const addFeatureEdit = (featureValue: string) => {
    if (!editingData.features.includes(featureValue)) {
      setEditingData((prev) => ({ ...prev, features: [...prev.features, featureValue] }));
    }
  };

  const removeFeatureEdit = (featureValue: string) => {
    setEditingData((prev) => ({ ...prev, features: prev.features.filter((f) => f !== featureValue) }));
  };

  const addCustomFeatureEdit = (featureName: string) => {
    if (featureName.trim() && !editingData.features.includes(featureName.trim())) {
      setEditingData((prev) => ({ ...prev, features: [...prev.features, featureName.trim()] }));
    }
  };

  const saveEdit = async (levelId: number) => {
    setSavingEdit(true);
    try {
      const payload: any = {
        level_number: parseInt(String(editingData.level_number)),
        required_points: parseInt(String(editingData.required_points)),
        name: editingData.name.trim() || undefined,
        description: editingData.description.trim() || undefined,
      };
      if (editingData.features.length > 0) payload.features = editingData.features;

      const { level } = await taskService.updateLevel(levelId, payload);
      setLevels((list) => list.map((it) => (it.id === levelId ? level : it)).sort((a, b) => a.level_number - b.level_number));
      cancelEdit();
      showToast('Cập nhật cấp độ thành công', 'success');
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Không thể cập nhật cấp độ', 'error');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteLevel = async (level: Level) => {
    if (!window.confirm(`Xóa cấp độ "${level.name || level.level_number}"?`)) return;
    try {
      await taskService.deleteLevel(level.id);
      setLevels((list) => list.filter((it) => it.id !== level.id));
      if (editingLevelId === level.id) {
        cancelEdit();
      }
      showToast('Xóa cấp độ thành công', 'success');
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Không thể xóa cấp độ', 'error');
    }
  };

  return (
    <>
      <div className="bg-white shadow rounded-xl border border-gray-200 mb-6">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-medium">Thêm cấp độ mới</h2>
        </div>
        <form className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4" onSubmit={handleCreateLevel}>
          <label className="text-sm">
            <span className="text-gray-700">Số cấp độ*</span>
            <input
              type="number"
              min="1"
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              value={newLevel.level_number}
              onChange={(e) => setNewLevel((prev) => ({ ...prev, level_number: parseInt(e.target.value) || 1 }))}
              required
            />
          </label>
          <label className="text-sm">
            <span className="text-gray-700">Điểm yêu cầu*</span>
            <input
              type="number"
              min="0"
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              value={newLevel.required_points}
              onChange={(e) => setNewLevel((prev) => ({ ...prev, required_points: parseInt(e.target.value) || 0 }))}
              required
            />
          </label>
          <label className="text-sm">
            <span className="text-gray-700">Tên cấp độ</span>
            <input
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              value={newLevel.name}
              onChange={(e) => setNewLevel((prev) => ({ ...prev, name: e.target.value }))}
            />
          </label>
          <label className="text-sm">
            <span className="text-gray-700">Mô tả</span>
            <input
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              value={newLevel.description}
              onChange={(e) => setNewLevel((prev) => ({ ...prev, description: e.target.value }))}
            />
          </label>
          <div className="text-sm md:col-span-2">
            <span className="text-gray-700 block mb-2">Tính năng mở khóa</span>
            
            {/* Danh sách tính năng đã chọn */}
            {newLevel.features.length > 0 && (
              <div className="mb-3 flex flex-wrap gap-2">
                {newLevel.features.map((feature, idx) => {
                  const featureLabel = COMMON_FEATURES.find((f) => f.value === feature)?.label || feature;
                  return (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                    >
                      {featureLabel}
                      <button
                        type="button"
                        onClick={() => removeFeature(feature)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
            )}

            {/* Chọn tính năng phổ biến */}
            <div className="mb-3">
              <div className="text-xs text-gray-600 mb-2">Chọn từ danh sách:</div>
              <div className="flex flex-wrap gap-2">
                {COMMON_FEATURES.map((feature) => (
                  <button
                    key={feature.value}
                    type="button"
                    onClick={() => addFeature(feature.value)}
                    disabled={newLevel.features.includes(feature.value)}
                    className={`px-3 py-1 rounded-lg text-sm border ${
                      newLevel.features.includes(feature.value)
                        ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {feature.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Nhập tính năng tùy chỉnh */}
            <div>
              <div className="text-xs text-gray-600 mb-2">Hoặc nhập tính năng tùy chỉnh:</div>
              <div className="flex gap-2">
                <input
                  type="text"
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="Ví dụ: unlock_special_feature"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addCustomFeature((e.target as HTMLInputElement).value);
                      (e.target as HTMLInputElement).value = '';
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={(e) => {
                    const input = (e.target as HTMLElement).previousElementSibling as HTMLInputElement;
                    if (input) {
                      addCustomFeature(input.value);
                      input.value = '';
                    }
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Thêm
                </button>
              </div>
            </div>
          </div>
          <div className="md:col-span-2 flex justify-end">
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50"
              disabled={creating}
            >
              {creating ? 'Đang tạo...' : 'Tạo cấp độ'}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white shadow rounded-xl border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-medium">Danh sách cấp độ</h2>
        </div>
        <div className="p-4 overflow-x-auto">
          {loading ? (
            <div className="text-center py-8">Đang tải...</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-600">
                  <th className="py-2 pr-4">ID</th>
                  <th className="py-2 pr-4">Số cấp độ</th>
                  <th className="py-2 pr-4">Tên</th>
                  <th className="py-2 pr-4">Điểm yêu cầu</th>
                  <th className="py-2 pr-4">Mô tả</th>
                  <th className="py-2 pr-4">Tính năng</th>
                  <th className="py-2 pr-4">Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {levels.map((level) => {
                  const isEditing = editingLevelId === level.id;
                  return (
                    <tr key={level.id} className="border-t border-gray-100">
                      <td className="py-2 pr-4">{level.id}</td>
                      <td className="py-2 pr-4">
                        {isEditing ? (
                          <input
                            type="number"
                            min="1"
                            className="border border-gray-300 rounded-lg px-2 py-1 w-full"
                            value={editingData.level_number}
                            onChange={(e) => setEditingData((prev) => ({ ...prev, level_number: parseInt(e.target.value) || 1 }))}
                          />
                        ) : (
                          level.level_number
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        {isEditing ? (
                          <input
                            className="border border-gray-300 rounded-lg px-2 py-1 w-full"
                            value={editingData.name}
                            onChange={(e) => setEditingData((prev) => ({ ...prev, name: e.target.value }))}
                          />
                        ) : (
                          level.name || '-'
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        {isEditing ? (
                          <input
                            type="number"
                            min="0"
                            className="border border-gray-300 rounded-lg px-2 py-1 w-full"
                            value={editingData.required_points}
                            onChange={(e) => setEditingData((prev) => ({ ...prev, required_points: parseInt(e.target.value) || 0 }))}
                          />
                        ) : (
                          level.required_points
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        {isEditing ? (
                          <input
                            className="border border-gray-300 rounded-lg px-2 py-1 w-full"
                            value={editingData.description}
                            onChange={(e) => setEditingData((prev) => ({ ...prev, description: e.target.value }))}
                          />
                        ) : (
                          <span className="text-gray-600">{level.description || '-'}</span>
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        {isEditing ? (
                          <div className="w-64">
                            {/* Danh sách tính năng đã chọn */}
                            {editingData.features.length > 0 && (
                              <div className="mb-2 flex flex-wrap gap-1">
                                {editingData.features.map((feature, idx) => {
                                  const featureLabel = COMMON_FEATURES.find((f) => f.value === feature)?.label || feature;
                                  return (
                                    <span
                                      key={idx}
                                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs"
                                    >
                                      {featureLabel}
                                      <button
                                        type="button"
                                        onClick={() => removeFeatureEdit(feature)}
                                        className="text-blue-600 hover:text-blue-800"
                                      >
                                        ×
                                      </button>
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                            {/* Chọn tính năng */}
                            <div className="mb-2">
                              <select
                                className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                                onChange={(e) => {
                                  if (e.target.value) {
                                    addFeatureEdit(e.target.value);
                                    e.target.value = '';
                                  }
                                }}
                              >
                                <option value="">Chọn tính năng...</option>
                                {COMMON_FEATURES.filter((f) => !editingData.features.includes(f.value)).map((feature) => (
                                  <option key={feature.value} value={feature.value}>
                                    {feature.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            {/* Nhập tùy chỉnh */}
                            <div className="flex gap-1">
                              <input
                                type="text"
                                className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs"
                                placeholder="Tùy chỉnh..."
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    addCustomFeatureEdit((e.target as HTMLInputElement).value);
                                    (e.target as HTMLInputElement).value = '';
                                  }
                                }}
                              />
                              <button
                                type="button"
                                onClick={(e) => {
                                  const input = (e.target as HTMLElement).previousElementSibling as HTMLInputElement;
                                  if (input) {
                                    addCustomFeatureEdit(input.value);
                                    input.value = '';
                                  }
                                }}
                                className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300"
                              >
                                +
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-xs max-w-xs">
                            {level.features && level.features.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {level.features.map((f, i) => {
                                  const featureLabel = COMMON_FEATURES.find((feat) => feat.value === f)?.label || f;
                                  return (
                                    <span key={i} className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs">
                                      {featureLabel}
                                    </span>
                                  );
                                })}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="py-2 pr-4 space-x-2 whitespace-nowrap">
                        {isEditing ? (
                          <>
                            <button
                              onClick={() => saveEdit(level.id)}
                              className="px-3 py-1 rounded-lg bg-green-600 text-white"
                              disabled={savingEdit}
                            >
                              {savingEdit ? 'Đang lưu...' : 'Lưu'}
                            </button>
                            <button onClick={cancelEdit} className="px-3 py-1 rounded-lg bg-gray-200 text-gray-700">
                              Hủy
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => startEdit(level)}
                              className="px-3 py-1 rounded-lg bg-blue-600 text-white"
                            >
                              Sửa
                            </button>
                            <button
                              onClick={() => handleDeleteLevel(level)}
                              className="px-3 py-1 rounded-lg bg-red-600 text-white"
                            >
                              Xóa
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminLevelsPage;

